// services/subtitleApi.js
import axios from "axios";
import { makeRequest } from "../utils/handleJWT";
import { checkErrorMessage, checkResponseData } from "../utils/responseHandler";
import { convertCuesToSRT } from "../utils/formatTime";

const SUBTITLE_API_URL = "https://umlang.com/subtitles/api/v1/subtitles";
const SUBTITLE_STORAGE_URL = "https://sub.umlang.com/subs";

// ============== APIهای بدون نیاز به توکن ==============

// جستجوی زیرنویس بر اساس نام فیلم
export const findCollectionSubtitlesByName = async (movieFileName) => {
  const url = `${SUBTITLE_API_URL}/findCollectionSubtitlesByName`;

  try {
    const response = await axios.post(url, {
      movieFileName,
    });
    return response.data;
  } catch (error) {
    console.error("Error in findCollectionSubtitlesByName:", error);
    throw error;
  }
};

// جستجوی مجموعه بر اساس اطلاعات فیلم (نام، فصل، قسمت، نوع)
export const findCollectionSubtitlesByInfo = async (
  movieName,
  season,
  episode,
  videoType = "movie"
) => {
  const url = `${SUBTITLE_API_URL}/findCollectionSubtitlesByInfo`;

  try {
    const body = {
      movieName,
      videoType,
    };

    if (videoType === "series") {
      body.season = season ? parseInt(season) : undefined;
      body.episode = episode ? parseInt(episode) : undefined;
    }

    const response = await axios.post(url, body);
    return response.data;
  } catch (error) {
    console.error("Error in findCollectionSubtitlesByInfo:", error);
    throw error;
  }
};

// ============== APIهای نیازمند توکن ==============

// دریافت زیرنویس با POST (برای آی‌دی‌های خاص)
export const getSubtitlesById = async (subtitlesUploadId) => {
  return makeRequest(async () => {
    const token = localStorage.getItem("token");
    const url = `${SUBTITLE_API_URL}/getSubtitles`;

    const response = await axios.post(
      url,
      {
        subtitlesUploadId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "text",
      }
    );

    return response.data;
  });
};

// 🆕 دریافت اطلاعات کامل یک زیرنویس با استفاده از subtitlesUploadId
export const findSubtitlesWithId = async (subtitlesUploadId) => {
  return makeRequest(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("لطفاً ابتدا وارد شوید");
    }

    try {
      const response = await axios.post(
        `${SUBTITLE_API_URL}/findSubtitlesWithId`,
        { subtitlesUploadId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const responseDetails = checkResponseData(response);

      if (responseDetails.success) {
        const subtitleData = responseDetails.data?.data?.[0] || null;
        return { success: true, data: subtitleData };
      }

      throw new Error(
        responseDetails.message || "خطا در دریافت اطلاعات زیرنویس"
      );
    } catch (error) {
      if (error.response) {
        const errorDetails = checkErrorMessage(error);
        throw new Error(
          errorDetails.message || "خطا در دریافت اطلاعات زیرنویس"
        );
      }
      throw error;
    }
  });
};

// services/subtitleApi.js - جایگزینی تابع uploadNonOnlineSubtitle

// 🆕 آپلود زیرنویس غیرآنلاین به بک‌اند با استفاده از multipart/form-data
export const uploadNonOnlineSubtitle = async (subtitleData, cues) => {
  return makeRequest(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("لطفاً ابتدا وارد شوید");
    }

    // تبدیل cues به فرمت SRT (در کلاینت)
    const srtContent = convertCuesToSRT(cues);

    // ساخت FormData
    const formData = new FormData();

    // اضافه کردن فایل (به صورت Blob)
    const fileBlob = new Blob([srtContent], { type: "text/plain" });
    const fileName = `${Date.now()}_${
      subtitleData.originalFileName || "subtitle.srt"
    }`;
    formData.append("subtitleFile", fileBlob, fileName);

    // اضافه کردن سایر فیلدها به صورت JSON string
    const metadata = {
      subtitlesLanguage: subtitleData.language || "",
      nameOfMovie: subtitleData.nameOfMovie || "",
      season: subtitleData.season || "",
      episode: subtitleData.episode || "",
      otherTags: subtitleData.otherTags || [],
      productionYear: subtitleData.productionYear || "",
      sourceType: subtitleData.sourceType || "local",
      originalFileName: subtitleData.originalFileName || fileName,
      movieType: subtitleData.videoType || "movie",
      coverUrl: subtitleData.coverUrl || "https://umlang.com/img/srt/srt.png",
    };

    formData.append("metadata", JSON.stringify(metadata));

    try {
      const response = await axios.post(
        `${SUBTITLE_API_URL}/uploadSubftpFromData`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const responseDetails = checkResponseData(response);

      if (responseDetails.success) {
        const subtitlesUploadId =
          responseDetails.data?.data?.[0]?.subtitlesUploadId ||
          responseDetails.data?.subtitlesUploadId ||
          response.data.subtitlesUploadId;

        if (subtitlesUploadId) {
          return { success: true, subtitlesUploadId };
        }

        throw new Error("شناسه زیرنویس در پاسخ یافت نشد");
      }

      throw new Error(responseDetails.message || "خطا در آپلود زیرنویس");
    } catch (error) {
      if (error.response) {
        const errorDetails = checkErrorMessage(error);
        throw new Error(errorDetails.message || "خطا در آپلود زیرنویس");
      }
      throw error;
    }
  });
};

// 🆕 ایجاد کالکشن زیرنویس
export const createCollection = async (data) => {
  return makeRequest(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("لطفاً ابتدا وارد شوید");
    }

    try {
      const response = await axios.post(
        `${SUBTITLE_API_URL}/addCollectionSubtitles`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // 🎯 استفاده از responseHandler برای پردازش پاسخ
      const responseDetails = checkResponseData(response);

      if (responseDetails.success) {
        return { success: true, data: response.data };
      }

      throw new Error(responseDetails.message || "خطا در ایجاد کالکشن");
    } catch (error) {
      // 🎯 استفاده از checkErrorMessage برای استخراج پیام خطای مناسب
      if (error.response) {
        const errorDetails = checkErrorMessage(error);
        throw new Error(errorDetails.message || "خطا در ایجاد کالکشن");
      }
      throw error;
    }
  });
};

// 🆕 جستجوی کاور فیلم
export const searchCover = async (nameOfMovie) => {
  return makeRequest(async () => {
    const token = localStorage.getItem("token");

    try {
      const response = await axios.post(
        `${SUBTITLE_API_URL}/findeCover?limit=50&page=1`,
        { nameOfMovie },
        {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
            "Content-Type": "application/json",
          },
        }
      );

      const responseDetails = checkResponseData(response);

      if (responseDetails.success) {
        const docs = responseDetails.data?.docs || [];
        const images = docs
          .filter((doc) => doc.fileUri)
          .map((doc) => doc.fileUri);
        return { success: true, images };
      }

      throw new Error(responseDetails.message || "خطا در جستجوی کاور");
    } catch (error) {
      if (error.response) {
        const errorDetails = checkErrorMessage(error);
        throw new Error(errorDetails.message || "خطا در جستجوی کاور");
      }
      throw error;
    }
  });
};

// 🆕 آپلود کاور فیلم
export const uploadCover = async (file, nameOfMovie) => {
  return makeRequest(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("لطفاً ابتدا وارد شوید");
    }

    try {
      const formData = new FormData();
      formData.append("coverFile", file);
      formData.append("nameOfMovie", nameOfMovie);

      const response = await axios.post(
        `${SUBTITLE_API_URL}/uploadCoverftp`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const responseDetails = checkResponseData(response);

      if (responseDetails.success) {
        const fileUri =
          responseDetails.data?.data?.[0]?.fileUri ||
          responseDetails.data?.fileUri;

        if (fileUri) {
          return { success: true, fileUri };
        }

        throw new Error("آدرس کاور در پاسخ یافت نشد");
      }

      throw new Error(responseDetails.message || "خطا در آپلود کاور");
    } catch (error) {
      if (error.response) {
        const errorDetails = checkErrorMessage(error);
        throw new Error(errorDetails.message || "خطا در آپلود کاور");
      }
      throw error;
    }
  });
};

// ============== توابع کمکی برای دریافت URL زیرنویس ==============

// تشخیص نوع زیرنویس بر اساس طول ID
export const getSubtitleUrl = (subtitlesUploadId) => {
  const idLength = subtitlesUploadId.length;

  switch (idLength) {
    case 10:
      return `${SUBTITLE_STORAGE_URL}/subtitles/${subtitlesUploadId}.srt`;
    case 12:
      return `${SUBTITLE_STORAGE_URL}/subtitles12/${subtitlesUploadId}.srt`;
    case 13:
      return `${SUBTITLE_STORAGE_URL}/subtitles13/${subtitlesUploadId}.srt`;
    case 14:
      return `${SUBTITLE_STORAGE_URL}/subtitles14/${subtitlesUploadId}.srt`;
    case 15:
      return `${SUBTITLE_STORAGE_URL}/subtitles15/${subtitlesUploadId}.srt`;
    default:
      return null;
  }
};

// دریافت محتوای زیرنویس (مدیریت خودکار نوع درخواست)
export const fetchSubtitleContent = async (subtitlesUploadId) => {
  const directUrl = getSubtitleUrl(subtitlesUploadId);

  if (directUrl) {
    const response = await fetch(directUrl);
    return await response.text();
  } else {
    return await getSubtitlesById(subtitlesUploadId);
  }
};
